import { useEffect, useMemo, useRef, useState } from 'react';

import { hasBlocks } from '../labEngine';
import { computeFDD } from './fddCalc';
import './FDDTest.css';

const n = (s) => (s === '' || s == null ? NaN : Number(s));
const blankDet = () => ({ cutterG: '', cutterSoilG: '', wetG: '', dryG: '', tareG: '' });
const DET_KEYS = ['cutterG', 'cutterSoilG', 'wetG', 'dryG', 'tareG'];
const detDone = (d) => DET_KEYS.every((k) => Number.isFinite(n(d[k])));
const detStarted = (d) => DET_KEYS.some((k) => d[k] !== '');
const toDet = (d) => Object.fromEntries(DET_KEYS.map((k) => [k, n(d[k])]));

// Field dry density by core cutter — IS 2720 (Part 29):1975.
// Project details live in the portal. `others` = reports of the other selected tests (used to offer the GSA value).
export default function FDDTest({ others, onReport }) {
  const [info, setInfo] = useState({ layer: '', side: '', source: '' });
  const [ref, setRef] = useState({ mdd: '', omc: '', passing: '', dia: '10', height: '13', volume: '1021' });
  const [dets, setDets] = useState(() => Array.from({ length: 3 }, blankDet));
  const setI = (k) => (e) => setInfo((s) => ({ ...s, [k]: e.target.value }));
  const setR = (k) => (e) => setRef((s) => ({ ...s, [k]: e.target.value }));
  const setD = (i, k) => (e) => setDets((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));

  const gsaPass = others?.gsa?.payload?.result?.rows?.find((r) => r.size === 4.75)?.pctPassing;
  const mddRes = others?.mdd?.payload?.result; // MDD / OMC from the compaction test, if filled
  const mddRef = mddRes && Number.isFinite(mddRes.mdd) ? mddRes : null;

  const done = dets.filter(detDone);
  const refMissing = [['lab MDD', ref.mdd], ['% passing 4.75 mm', ref.passing], ['cutter diameter', ref.dia], ['cutter height', ref.height], ['cutter volume', ref.volume]]
    .filter(([, v]) => !Number.isFinite(n(v))).map(([l]) => l);
  const canCompute = refMissing.length === 0 && done.length > 0;

  const inputs = useMemo(() => canCompute ? {
    diameterCm: n(ref.dia), heightCm: n(ref.height), volumeCc: n(ref.volume),
    passing475Pct: n(ref.passing), labMddGcc: n(ref.mdd), omcPct: ref.omc === '' ? null : n(ref.omc),
    layer: info.layer.trim(), side: info.side, source: info.source.trim(),
    dets: done.map(toDet),
  } : null, [canCompute, ref, info, dets]); // eslint-disable-line react-hooks/exhaustive-deps

  const { result, issues } = useMemo(() => (inputs ? computeFDD(inputs) : { result: null, issues: [] }), [inputs]);
  const ready = !!result && !hasBlocks(issues);
  const dirty = dets.some(detStarted) || ref.mdd !== '' || ref.omc !== '' || ref.passing !== ''
    || info.layer !== '' || info.side !== '' || info.source !== '';

  const hint = ready ? '' : !dirty ? 'enter the readings'
    : refMissing.length ? `fill ${refMissing[0]}${refMissing.length > 1 ? ` (+${refMissing.length - 1} more)` : ''}`
      : !done.length ? 'complete a determination'
        : (issues.find((i) => i.level === 'block')?.msg || '');

  const report = useMemo(() => ({
    status: ready ? 'ready' : dirty ? 'incomplete' : 'empty',
    ready, dirty, hint,
    summary: ready ? `Dry density ${result.dryDensity.toFixed(2)} · Compaction ${result.compactionPct.toFixed(1)} %` : '',
    payload: ready ? { inputs, result, issues } : null,
  }), [ready, dirty, hint, result, issues, inputs]);

  const cb = useRef(onReport); cb.current = onReport;
  useEffect(() => { cb.current?.(report); }, [report]);

  // live dry density per determination, shown once the row is complete and the volume is known
  const rowDry = (d) => {
    if (!detDone(d) || !(n(ref.volume) > 0)) return null;
    const dd = toDet(d);
    if (!(dd.cutterSoilG > dd.cutterG) || !(dd.dryG > dd.tareG) || !(dd.wetG >= dd.dryG)) return null;
    const bulk = (dd.cutterSoilG - dd.cutterG) / n(ref.volume);
    const w = ((dd.wetG - dd.dryG) / (dd.dryG - dd.tareG)) * 100;
    return { w, dry: (100 * bulk) / (100 + w) };
  };

  const num = (value, onChange, label, step = '0.01') => (
    <input className="fdd-input" type="number" step={step} min="0" inputMode="decimal" aria-label={label} value={value} onChange={onChange} />
  );

  return (
    <div className="fdd">
      <section>
        <div className="fdd-sec-head"><h4>Test details</h4><span className="fdd-muted">location and chainage come from the project details above</span></div>
        <div className="fdd-three">
          <label className="fdd-field">Layer
            <input className="fdd-input" list="fdd-layers" value={info.layer} onChange={setI('layer')} placeholder="Sub Grade" />
            <datalist id="fdd-layers"><option value="Sub Grade" /><option value="Embankment" /><option value="GSB" /><option value="WMM" /></datalist></label>
          <label className="fdd-field">Side
            <select className="fdd-input" value={info.side} onChange={setI('side')}>
              <option value="">—</option><option value="LHS">LHS</option><option value="RHS">RHS</option><option value="CL">CL</option>
            </select></label>
          <label className="fdd-field">Source of material
            <input className="fdd-input" value={info.source} onChange={setI('source')} placeholder="Existing Soil" /></label>
        </div>
      </section>

      <section>
        <div className="fdd-sec-head"><h4>Lab reference &amp; core cutter</h4></div>
        <div className="fdd-three">
          <label className="fdd-field">Lab MDD (g/cc) *{num(ref.mdd, setR('mdd'), 'Lab MDD', '0.001')}
            {mddRef && ref.mdd === '' && (
              <button type="button" className="fdd-link" onClick={() => setRef((s) => ({ ...s, mdd: mddRef.mdd.toFixed(2), omc: s.omc === '' ? mddRef.omc.toFixed(1) : s.omc }))}>
                Use {mddRef.mdd.toFixed(2)} g/cc from MDD
              </button>)}</label>
          <label className="fdd-field">OMC (%) <span className="fdd-opt">optional</span>{num(ref.omc, setR('omc'), 'OMC')}</label>
          <label className="fdd-field">% passing 4.75 mm *{num(ref.passing, setR('passing'), 'Passing 4.75 mm', '0.1')}
            {Number.isFinite(gsaPass) && ref.passing === '' && (
              <button type="button" className="fdd-link" onClick={() => setRef((s) => ({ ...s, passing: gsaPass.toFixed(1) }))}>
                Use {gsaPass.toFixed(1)} % from GSA
              </button>)}
            <span className="fdd-muted">needs to be 90 % or more</span></label>
          <label className="fdd-field">Cutter diameter (cm){num(ref.dia, setR('dia'), 'Cutter diameter', '0.1')}</label>
          <label className="fdd-field">Cutter height (cm){num(ref.height, setR('height'), 'Cutter height', '0.1')}</label>
          <label className="fdd-field">Cutter volume (cc){num(ref.volume, setR('volume'), 'Cutter volume', '0.1')}</label>
        </div>
      </section>

      <section>
        <div className="fdd-sec-head"><h4>Determinations</h4><span className="fdd-muted">minimum 3</span></div>
        <div className="fdd-scroll">
          <div className="fdd-grid fdd-labels">
            <span>#</span><span>Cutter (g)</span><span>Cutter + wet soil (g)</span><span>Container + wet (g)</span>
            <span>Container + dry (g)</span><span>Container (g)</span><span>Dry density</span>
          </div>
          {dets.map((d, i) => {
            const r = rowDry(d);
            return (
              <div key={i} className="fdd-grid fdd-det">
                <span className="fdd-no">{i + 1}</span>
                {DET_KEYS.map((k, c) => (
                  <input key={k} className="fdd-input" type="number" step={c < 2 ? '1' : '0.01'} min="0" inputMode="decimal"
                    aria-label={`Determination ${i + 1} ${k}`} value={d[k]} onChange={setD(i, k)} />
                ))}
                <span className="fdd-calc">{r ? <>{r.dry.toFixed(3)}<small>w {r.w.toFixed(1)}%</small></> : '—'}</span>
                {dets.length > 3 && <button type="button" className="fdd-x" aria-label={`Remove determination ${i + 1}`}
                  onClick={() => setDets((rows) => rows.filter((_, j) => j !== i))}>×</button>}
              </div>
            );
          })}
        </div>
        <button type="button" className="fdd-link" onClick={() => setDets((r) => [...r, blankDet()])}>+ Add determination</button>
      </section>

      {result && (
        <section className="fdd-result">
          <div className="fdd-stat"><span>Dry density</span><b>{result.dryDensity.toFixed(2)}</b><small>g/cc (average)</small></div>
          <div className="fdd-stat"><span>Moisture content</span><b>{result.mcAvg.toFixed(1)}</b><small>%</small></div>
          <div className="fdd-stat"><span>Compaction</span><b>{result.compactionPct.toFixed(1)}</b><small>% of lab MDD</small></div>
        </section>
      )}

      {issues.length > 0 && (
        <ul className="fdd-issues">
          {issues.map((it, i) => (
            <li key={i} className={it.level === 'block' ? 'fdd-err' : 'fdd-warn'}>
              <b>{it.level === 'block' ? 'Fix' : 'Check'}</b> {it.msg} <span className="fdd-muted">({it.clause})</span>
            </li>
          ))}
        </ul>
      )}
      {dirty && refMissing.length > 0 && <p className="fdd-missing"><b>Still to fill:</b> {refMissing.join(', ')}</p>}
    </div>
  );
}
